import { Control, Controller, FieldErrors } from 'react-hook-form';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { Button } from '@/shared/components/ui/button';
import { X } from 'lucide-react';
import { useState } from 'react';
import type { ProviderEditorFormData } from '../schemas';

type ProfessionalIdentitySectionProps = {
  control: Control<ProviderEditorFormData>;
  errors: FieldErrors<ProviderEditorFormData>;
};

type FormFieldProps = {
  label: string;
  required?: boolean;
  error?: string;
  htmlFor?: string;
  children: React.ReactNode;
  hint?: string;
};

function FormField({
  label,
  required = false,
  error,
  htmlFor,
  children,
  hint,
}: FormFieldProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor} className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </Label>
      {children}
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

/**
 * ArrayFieldManager - Reusable component for managing array fields
 */
function ArrayFieldManager({
  label,
  value,
  onChange,
  placeholder,
  hint,
}: {
  label: string;
  value: string[];
  onChange: (newValue: string[]) => void;
  placeholder: string;
  hint?: string;
}) {
  const [inputValue, setInputValue] = useState('');

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
      setInputValue('');
    }
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium text-gray-700">{label}</Label>

      <div className="flex gap-2">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1"
        />
        <Button type="button" variant="outline" size="sm" onClick={handleAdd}>
          Add
        </Button>
      </div>

      {hint && <p className="text-xs text-gray-500">{hint}</p>}

      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((item, index) => (
            <div
              key={index}
              className="inline-flex items-center gap-2 rounded-md bg-blue-50 px-3 py-1 text-sm text-blue-700"
            >
              <span>{item}</span>
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="text-blue-500 hover:text-blue-700"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * ProfessionalIdentitySection - Provider's professional identity and values
 * 
 * Contains:
 * - Target age range
 * - Languages spoken
 * - Care philosophy
 * - Why they do this work
 */
export function ProfessionalIdentitySection({
  control,
  errors,
}: ProfessionalIdentitySectionProps) {
  return (
    <div className="space-y-6">
      {/* Target Age Range */}
      <FormField
        label="Target Age Range"
        htmlFor="target-age-range"
        error={errors.provider?.targetAgeRange?.message}
        hint="Age group served (max 100 characters)"
      >
        <Controller
          name="provider.targetAgeRange"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              value={field.value || ''}
              id="target-age-range"
              maxLength={100}
              className={errors.provider?.targetAgeRange ? 'border-red-500' : ''}
            />
          )}
        />
      </FormField>

      {/* Languages */}
      <Controller
        name="provider.languages"
        control={control}
        render={({ field }) => (
          <ArrayFieldManager
            label="Languages Spoken"
            value={field.value || []}
            onChange={field.onChange}
            placeholder=""
            hint="Add languages your team speaks"
          />
        )}
      />

      {/* Care Philosophy */}
      <FormField
        label="Care Philosophy"
        htmlFor="care-philosophy"
        error={errors.provider?.carePhilosophy?.message}
        hint="Describe your approach to care and treatment philosophy"
      >
        <Controller
          name="provider.carePhilosophy"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              value={field.value || ''}
              id="care-philosophy"
              rows={5}
              className={errors.provider?.carePhilosophy ? 'border-red-500' : ''}
            />
          )}
        />
      </FormField>

      {/* Why This Work */}
      <FormField
        label="Why I Do This Work"
        htmlFor="why-this-work"
        error={errors.provider?.whyThisWork?.message}
        hint="Share your personal motivation and story"
      >
        <Controller
          name="provider.whyThisWork"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              value={field.value || ''}
              id="why-this-work"
              rows={5}
              className={errors.provider?.whyThisWork ? 'border-red-500' : ''}
            />
          )}
        />
      </FormField>
    </div>
  );
}

